import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { orderService, customOrderService, productService, categoryService } from '../services/firestore';
import { Order, CustomOrder, Product, Category } from '../types';
import ImageUpload from '../components/ImageUpload';
import './AdminPage.css';

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'orders' | 'custom-orders' | 'products' | 'categories'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [customOrders, setCustomOrders] = useState<CustomOrder[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    imageUrl: ''
  });
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    imageUrl: '',
    stock: ''
  });
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      return;
    }

    const fetchData = async () => {
      try {
        const [ordersData, customOrdersData, productsData, categoriesData] = await Promise.all([
          orderService.getAll(),
          customOrderService.getAll(),
          productService.getAll(),
          categoryService.getAll()
        ]);
        
        setOrders(ordersData);
        setCustomOrders(customOrdersData);
        setProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleOrderStatusUpdate = async (orderId: string, status: Order['orderStatus']) => {
    try {
      await orderService.updateStatus(orderId, status);
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, orderStatus: status } : order
      ));
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handleCustomOrderStatusUpdate = async (orderId: string, status: CustomOrder['status']) => {
    try {
      await customOrderService.updateStatus(orderId, status);
      setCustomOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, status } : order
      ));
    } catch (error) {
      console.error('Error updating custom order status:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'accepted': return '#17a2b8';
      case 'ready_to_out': return '#6610f2';
      case 'delivered': return '#28a745';
      case 'cancelled': return '#dc3545';
      case 'contacted': return '#17a2b8';
      case 'completed': return '#28a745';
      default: return '#6c757d';
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      const categoryData = {
        name: newCategory.name.trim(),
        description: newCategory.description.trim(),
        imageUrl: newCategory.imageUrl.trim() || '/api/placeholder/300/200'
      };

      if (editingCategory) {
        // Update existing category
        await categoryService.update(editingCategory.id, categoryData);
        setCategories(categories.map(cat => 
          cat.id === editingCategory.id 
            ? { ...cat, ...categoryData }
            : cat
        ));
        alert('Category updated successfully!');
      } else {
        // Add new category
        const newCategoryDoc = await categoryService.create(categoryData);
        setCategories([...categories, newCategoryDoc]);
        alert('Category added successfully!');
      }
      
      // Reset form
      setNewCategory({ name: '', description: '', imageUrl: '' });
      setShowCategoryForm(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category. Please try again.');
    }
  };

  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      description: category.description,
      imageUrl: category.imageUrl
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoryService.delete(categoryId);
        setCategories(categories.filter(cat => cat.id !== categoryId));
        alert('Category deleted successfully!');
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category. Please try again.');
      }
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) {
      alert('Please enter a product name');
      return;
    }
    
    if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
      alert('Please enter a valid price');
      return;
    }
    
    if (!editingProduct && !newProduct.categoryId) {
      alert('Please select a category');
      return;
    }

    try {
      const selectedCategory = editingProduct 
        ? categories.find(cat => cat.name === editingProduct.category)
        : categories.find(cat => cat.id === newProduct.categoryId);
      
      const productData = {
        name: newProduct.name.trim(),
        description: newProduct.description.trim(),
        price: parseFloat(newProduct.price),
        category: selectedCategory?.name || editingProduct?.category || '',
        imageUrl: newProduct.imageUrl.trim() || '/api/placeholder/300/300',
        stock: parseInt(newProduct.stock) || 0
      };

      if (editingProduct) {
        // Update existing product
        await productService.update(editingProduct.id, productData);
        setProducts(products.map(prod => 
          prod.id === editingProduct.id 
            ? { ...prod, ...productData }
            : prod
        ));
        alert('Product updated successfully!');
      } else {
        // Add new product
        const newProductDoc = await productService.create(productData);
        setProducts([...products, newProductDoc]);
        alert('Product added successfully!');
      }
      
      // Reset form
      setNewProduct({ 
        name: '', 
        description: '', 
        price: '', 
        categoryId: '', 
        imageUrl: '', 
        stock: '' 
      });
      setShowProductForm(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product. Please try again.');
    }
  };

  const handleProductInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      categoryId: '',
      imageUrl: product.imageUrl,
      stock: product.stock.toString()
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.delete(productId);
        setProducts(products.filter(prod => prod.id !== productId));
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <p>Manage orders, products, and categories</p>
        </div>

        <div className="admin-tabs">
          <button
            className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders ({orders.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'custom-orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('custom-orders')}
          >
            Custom Orders ({customOrders.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products ({products.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories ({categories.length})
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'orders' && (
            <div className="orders-section">
              <h2>Customer Orders</h2>
              {orders.length === 0 ? (
                <p>No orders found.</p>
              ) : (
                <div className="orders-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td>#{order.id.slice(-8)}</td>
                          <td>
                            <div>
                              <strong>{order.customerName}</strong>
                              <br />
                              <small>{order.customerPhone}</small>
                            </div>
                          </td>
                          <td>{order.items.length} items</td>
                          <td>{formatPrice(order.totalAmount)}</td>
                          <td>
                            <span className={`payment-badge ${order.paymentStatus}`}>
                              {order.paymentStatus}
                            </span>
                          </td>
                          <td>
                            <span 
                              className="status-badge"
                              style={{ backgroundColor: getStatusColor(order.orderStatus) }}
                            >
                              {order.orderStatus}
                            </span>
                          </td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>
                            <select
                              value={order.orderStatus}
                              onChange={(e) => handleOrderStatusUpdate(order.id, e.target.value as Order['orderStatus'])}
                              className="status-select"
                            >
                              <option value="pending">Pending</option>
                              <option value="accepted">Accepted</option>
                              <option value="ready_to_out">Ready to Out</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'custom-orders' && (
            <div className="custom-orders-section">
              <h2>Custom Orders</h2>
              {customOrders.length === 0 ? (
                <p>No custom orders found.</p>
              ) : (
                <div className="custom-orders-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customOrders.map((order) => (
                        <tr key={order.id}>
                          <td>#{order.id.slice(-8)}</td>
                          <td>
                            <div>
                              <strong>{order.customerName}</strong>
                              <br />
                              <small>{order.customerPhone}</small>
                            </div>
                          </td>
                          <td>
                            <div className="description-cell">
                              {order.description}
                            </div>
                          </td>
                          <td>
                            <span 
                              className="status-badge"
                              style={{ backgroundColor: getStatusColor(order.status) }}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>
                            <select
                              value={order.status}
                              onChange={(e) => handleCustomOrderStatusUpdate(order.id, e.target.value as CustomOrder['status'])}
                              className="status-select"
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'products' && (
            <div className="products-section">
              <h2>Products Management</h2>
              <div className="section-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowProductForm(true)}
                >
                  Add New Product
                </button>
              </div>

              {/* Product Form */}
              {showProductForm && (
                <div className="product-form">
                  <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                  <div className="form-group">
                    <label htmlFor="productName">Product Name *</label>
                    <input
                      type="text"
                      id="productName"
                      name="name"
                      value={newProduct.name}
                      onChange={handleProductInputChange}
                      placeholder="Enter product name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productDescription">Description</label>
                    <textarea
                      id="productDescription"
                      name="description"
                      value={newProduct.description}
                      onChange={handleProductInputChange}
                      placeholder="Enter product description"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productPrice">Price (INR) *</label>
                    <input
                      type="number"
                      id="productPrice"
                      name="price"
                      value={newProduct.price}
                      onChange={handleProductInputChange}
                      placeholder="Enter product price"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productCategory">Category *</label>
                    <select
                      id="productCategory"
                      name="categoryId"
                      value={newProduct.categoryId}
                      onChange={handleProductInputChange}
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="productStock">Stock Quantity</label>
                    <input
                      type="number"
                      id="productStock"
                      name="stock"
                      value={newProduct.stock}
                      onChange={handleProductInputChange}
                      placeholder="Enter stock quantity"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Product Image</label>
                    <ImageUpload
                      value={newProduct.imageUrl}
                      onChange={(url) => setNewProduct(prev => ({ ...prev, imageUrl: url }))}
                      folder="products"
                      placeholder="Upload product image"
                    />
                  </div>
                  <div className="form-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleAddProduct}
                    >
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowProductForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {products.length === 0 ? (
                <p>No products found.</p>
              ) : (
                <div className="products-grid">
                  {products.map((product) => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        <img 
                          src={product.imageUrl || ''} 
                          alt={product.name}
                        />
                      </div>
                      <div className="product-info">
                        <h3>{product.name}</h3>
                        <p className="product-description">{product.description}</p>
                        <div className="product-meta">
                          <span className="price">{formatPrice(product.price)}</span>
                          <span className="stock">Stock: {product.stock}</span>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="categories-section">
              <h2>Categories Management</h2>
              <div className="section-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCategoryForm(true)}
                >
                  Add New Category
                </button>
              </div>

              {/* Category Form */}
              {showCategoryForm && (
                <div className="category-form">
                  <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
                  <div className="form-group">
                    <label htmlFor="name">Category Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newCategory.name}
                      onChange={handleCategoryInputChange}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={newCategory.description}
                      onChange={handleCategoryInputChange}
                      placeholder="Enter category description"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Category Image</label>
                    <ImageUpload
                      value={newCategory.imageUrl}
                      onChange={(url) => setNewCategory(prev => ({ ...prev, imageUrl: url }))}
                      folder="categories"
                      placeholder="Upload category image"
                    />
                  </div>
                  <div className="form-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleAddCategory}
                    >
                      {editingCategory ? 'Update Category' : 'Add Category'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowCategoryForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {categories.length === 0 ? (
                <p>No categories found.</p>
              ) : (
                <div className="categories-grid">
                  {categories.map((category) => (
                    <div key={category.id} className="category-card">
                      <div className="category-image">
                        <img 
                          src={category.imageUrl || ''} 
                          alt={category.name}
                        />
                      </div>
                      <div className="category-info">
                        <h3>{category.name}</h3>
                        <p className="category-description">{category.description}</p>
                        <div className="category-actions">
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEditCategory(category)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
